import { useEffect } from "react";
import { isAndroidDevice, isIosDevice } from "../../utils/browserUtils";
import { APPLE_APP_STORE_URL, GOOGLE_PLAY_STORE_URL } from "../../utils/constants";
import { useMonitoring } from "../../utils/monitoring-context/useMonitoring";
import "./AppRedirect.css";

type Platform = "ios" | "android" | "desktop";

// Time to let the RUM beacon flush before we navigate away (mobile unloads this page on redirect).
const REDIRECT_DELAY_MS = 400;

// Base for the public/ image assets. PUBLIC_URL keeps every reference correct if the app is ever
// served from a non-root base path; defined once so the background and every <img> stay consistent.
const IMAGES = `${process.env.PUBLIC_URL}/images`;

// Official tagline from the Card Clash wiki
// (https://w.amazon.com/bin/view/AWS_Immersive_Learning/Products/CardClash).
const DESCRIPTION =
  "A free, mobile-first architecture-design card game. Place AWS service cards into the right slots to " +
  "score — learning solution design by playing, solo or head-to-head.";

/**
 * One QR -> one URL (/get) landing that routes a visitor to the right AWS Card Clash store listing:
 * iOS -> App Store, Android -> Google Play. Desktop / unknown devices can't be auto-routed, so they
 * see both store buttons rather than a dead redirect (e.g. a laptop scanning the printed QR).
 *
 * The redirect uses window.location.replace so the store page doesn't land in history (the back
 * button returns the visitor to wherever they scanned from, not this bounce page). Before it fires
 * we record a RUM event tagged with the resolved platform, so /get usage is trackable by the same
 * iOS/Android/desktop decision the redirect makes (isIosDevice handles iPadOS-reports-as-Mac).
 */
export const AppRedirect = () => {
  const { awsRum } = useMonitoring();

  useEffect(() => {
    const platform: Platform = isIosDevice() ? "ios" : isAndroidDevice() ? "android" : "desktop";
    try {
      awsRum?.recordEvent("get_redirect", { platform });
    } catch {
      // Monitoring must never block the redirect.
    }

    const target = platform === "ios" ? APPLE_APP_STORE_URL : platform === "android" ? GOOGLE_PLAY_STORE_URL : null;
    if (!target) {
      return; // desktop / unknown: no auto-redirect — the buttons below are the path.
    }
    const timer = window.setTimeout(() => window.location.replace(target), REDIRECT_DELAY_MS);
    return () => window.clearTimeout(timer);
    // Run once on mount; awsRum is created synchronously by the provider before this mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="app-redirect" style={{ backgroundImage: `url(${IMAGES}/card-clash-hero.png)` }}>
      <div className="app-redirect__content">
        <img className="app-redirect__logo" src={`${IMAGES}/card-clash-logo.png`} alt="AWS Card Clash" />
        <h1 className="app-redirect__title">AWS Card Clash</h1>
        <p className="app-redirect__desc">{DESCRIPTION}</p>
        <div className="app-redirect__stores">
          <a className="store-link" href={APPLE_APP_STORE_URL} aria-label="Download on the App Store">
            <img
              className="store-link__badge store-link__badge--apple"
              src={`${IMAGES}/app-store-badge.svg`}
              alt="Download on the App Store"
            />
          </a>
          <a className="store-link" href={GOOGLE_PLAY_STORE_URL} aria-label="Get it on Google Play">
            <img
              className="store-link__badge store-link__badge--google"
              src={`${IMAGES}/google-play-badge.png`}
              alt="Get it on Google Play"
            />
          </a>
        </div>
      </div>
    </div>
  );
};
